import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CreateUserFormValidationSchema} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogStore} from '@ariakit/react'
import {Text} from '@/components/ui/text/Text'
import {remapUserToPayload} from '../../utils'
import {useCreateUser} from '../../queries/useCreateUser'
import {useQueryClient} from '@tanstack/react-query'
import {routes} from '@/utilities/constants/routes'
import {QUERY_KEYS} from '@/queryClient'
import {generatePath, useNavigate} from 'react-router-dom'
import {AxiosError} from 'axios'
import {zodResolver} from '@hookform/resolvers/zod'
import {toast} from '@/utilities/toast/toast'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'

export const CreateUserConfirmationModal = ({
    dialogStore,
    defaultValues,
    onErrorCallback
}: {
    dialogStore: DialogStore
    defaultValues: CreateUserFormValidationSchema
    onErrorCallback: (errors: AxiosError<{errors: {field: string; rule: string}[]}>) => void
}) => {
    const {t} = useTranslation()
    const form = useForm<CreateUserFormValidationSchema>({
        resolver: zodResolver(CreateUserFormValidationSchema),
        defaultValues
    })
    const firstName = useWatch({control: form.control, name: 'firstName'})
    const lastName = useWatch({control: form.control, name: 'lastName'})
    const email = useWatch({control: form.control, name: 'email'})
    const specialization = useWatch({control: form.control, name: 'specialization'})
    const role = useWatch({control: form.control, name: 'role'})
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const {mutate: createUserMutation, isPending: isPendingCreateUser} = useCreateUser({
        onSuccess: async user => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS]})
            toast.success(t('commons:has_been_added', {fullName: user.fullName}))
            navigate(generatePath(routes.USER_DETAILS.handle.path, {id: user.id}))
        },
        onError: onErrorCallback
    })

    const handleConfirmSubmit = form.handleSubmit(formValues =>
        createUserMutation(remapUserToPayload(formValues, true))
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPendingCreateUser}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('users:add_user_title')}</DialogHeading>
                        <Text size="textSm" palette="neutral" shade="600">
                            {lastName} {firstName} - {specialization.name}
                        </Text>
                    </Flexbox>
                }
                contentSlot={
                    role?.name && (
                        <>
                            <Text size="textSm" palette="neutral" shade="950">
                                {t('users:confirmation_modal_access_type')}
                            </Text>
                            <DialogHeading>{role.name}</DialogHeading>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t('users:confirmation_modal_paragraph', {email})}
                            </Text>
                        </>
                    )
                }
            />
        </FormProvider>
    )
}
